import {
    QualityApi,
    GetQualityPublishedformsEvaluationsEvaluationFormTypeEnum,
} from "@genesys/cloud-frontend/apis/QualityApi";
import type { EvaluationFormResponse } from "@genesys/cloud-frontend/models";
import { API_CONFIGURATION } from "util/apiUtils";

/**
 * Lightweight view of an evaluation form for the agent-scoring UI. Rules
 * reference forms by `contextId` (a stable id across form revisions), so that
 * is the value the dropdown stores and the table resolves names against.
 */
export interface EvaluationFormOption {
    contextId: string;
    name: string;
}

/**
 * Page size for the searchable form dropdown. Matches PDUI's EvaluationFormService
 * default (hardCodedPageSize = 25): one page on load / clear, with typeahead
 * name search for anything beyond that — no multi-page "load all".
 */
const FORM_SEARCH_PAGE_SIZE = 25;

function getQualityApi(): QualityApi {
    return new QualityApi(API_CONFIGURATION);
}

function toOption(form: EvaluationFormResponse): EvaluationFormOption | null {
    // Omit forms we can't meaningfully display: a missing contextId is unusable,
    // and a missing name should drop the form rather than surface a raw UUID as
    // its label. Every form is expected to have a name, so this is rare.
    if (!form.contextId || !form.name) return null;
    return { contextId: form.contextId, name: form.name };
}

/**
 * Searches published Automated evaluation forms (latest revision per context),
 * optionally filtered by name. Used to populate the editor's form dropdown.
 * Fetches a single page (parity with PDUI AgentScoringRulePage).
 */
export async function searchEvaluationForms(
    name?: string
): Promise<EvaluationFormOption[]> {
    const listing = await getQualityApi().getQualityPublishedformsEvaluations({
        pageSize: FORM_SEARCH_PAGE_SIZE,
        onlyLatestPerContext: true,
        evaluationFormType:
            GetQualityPublishedformsEvaluationsEvaluationFormTypeEnum.Automated,
        ...(name?.trim() ? { name: name.trim() } : {}),
    });

    return (listing.entities ?? [])
        .map(toOption)
        .filter((option): option is EvaluationFormOption => option !== null);
}

/**
 * Resolves evaluation form names for the given context ids via the bulk-context
 * endpoint. Used by `evaluationFormNamesSlice` (rules list "Rule Form" column)
 * and by the editor to resolve an inaccessible selected form's name
 * without going through that Redux cache.
 */
export async function getEvaluationFormsByContextIds(
    contextIds: string[]
): Promise<EvaluationFormOption[]> {
    const uniqueContextIds = [...new Set(contextIds.filter(Boolean))];
    if (uniqueContextIds.length === 0) {
        return [];
    }

    const forms = await getQualityApi().getQualityFormsEvaluationsBulkContexts({
        contextId: uniqueContextIds,
    });

    return forms
        .map(toOption)
        .filter((option): option is EvaluationFormOption => option !== null);
}
